import Header from "@modules/layout/header/header";
import { useAppContext } from "@context/app";
import type { LayoutInternalProps, LayoutProps, PageProps } from "@types";
import type { NextComponentType, NextPageContext } from "next";
import dynamic from "next/dynamic";
import ScrollToTop from "@modules/layout/scrollToTop";
import { useEffect } from "react";
import FetchingView from "@shared/components/fetching-view";
import { useCartContext } from "@context/cart";
import useRequest from "@shared/hooks/useRequest";
import { getCookie } from "@utils/Cookies";
import { COOKIE_NOT_FIRST_USE } from "@utils/constants";
import { isServer } from "@utils/helpers";
import Footer from "@modules/layout/footer/footer";
import classNames from "classnames";
import BottomTabNavigation from "@modules/layout/bottom-tabs";
import { useLayoutContext } from "@context/layout";
import FeedbackFab from "@modules/layout/feedback-fab";
import homeApi from "@features/home/helpers/homeApi";
import FestivalBannerPreload from "@modules/layout/FestivalBanner/PicturePreload";

const NProgress = dynamic(() => import("legacy/elements/nprogress"), {
  ssr: false,
});
const AlertUI = dynamic(() => import("./alert-ui"), { ssr: false });
const StaticToastUI = dynamic(() => import("./static-toast-ui"), {
  ssr: false,
});

const Layout: React.FC<LayoutInternalProps> = ({
  children,
  mainClassName,
  rootClassName,
  contentContainerClassName,
  footerProps,
  noMainTag,
  // pageProps,
  headerProps = {},
}) => {
  const Cookie = getCookie();
  const {
    hideFestivalBanner = false,
    showEmptyLayout = false,
    hideHeader = false,
  } = headerProps;
  const {
    setShowFestivalBanner,
    setUserLoggedIn,
    setLayoutLoaded,
    setUserId,
    hideFooter,
    hideTopbar,
    hideLayout,
    hideBottomNavigation,
  } = useAppContext();
  const { setCartItems } = useCartContext();
  if (!isServer()) console.log("780");
  const { userData, staticData, fetstivalBanner } = useLayoutContext();

  const { data: megamenu } = useRequest(homeApi.getMegamenu(), {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
  });

  useEffect(() => {
    if (userData?.user) {
      setUserLoggedIn(userData.is_login);
      setUserId(userData.user_id);
      setLayoutLoaded(true);
    }
    if (userData?.cart?.length) setCartItems(userData.cart || []);
    else setCartItems([]);
  }, [userData]);

  useEffect(() => {
    if (fetstivalBanner) {
      const hasActiveFestivalBanner = Boolean(fetstivalBanner?.is_active);
      setShowFestivalBanner(hasActiveFestivalBanner && !hideFestivalBanner);
    }
  }, [hideFestivalBanner, fetstivalBanner]);

  useEffect(() => {
    if (!(Cookie.get(COOKIE_NOT_FIRST_USE) === "true")) {
      Cookie.set(COOKIE_NOT_FIRST_USE, true, {}, 365 * 2);
    }
  }, []);

  const renderHeader = Boolean(!showEmptyLayout && !hideTopbar);

  const isLoadingLayout = !staticData;
  const bannerPreload = <FestivalBannerPreload disabled={hideFestivalBanner} />;

  if (isLoadingLayout) {
    return (
      <>
        {bannerPreload}
        <FetchingView />
      </>
    );
  }

  return (
    <div
      id="faradars-main"
      className={classNames(
        "flex min-h-screen flex-col bg-fds-on-inverse",
        rootClassName
      )}
    >
      {bannerPreload}
      {renderHeader ? (
        !hideLayout && !hideHeader ? (
          <Header megamenu={megamenu} hideFestivalBanner={hideFestivalBanner} />
        ) : null
      ) : null}
      <FeedbackFab />
      <div className="flex flex-col w-full flex-1 transition ease-linear duration-150">
        {noMainTag ? (
          <div className={classNames("flex flex-1", mainClassName)}>
            <div
              className={classNames(
                "relative flex w-full flex-col ",
                contentContainerClassName
              )}
            >
              {children}
            </div>
          </div>
        ) : (
          <main className={classNames("flex flex-1", mainClassName)}>
            <div
              className={classNames(
                "relative flex w-full flex-col ",
                contentContainerClassName
              )}
            >
              {children}
            </div>
          </main>
        )}
        {Boolean(hideLayout) ? null : (
          <>
            {hideFooter ? null : <Footer {...footerProps} />}
            {hideBottomNavigation ? null : <BottomTabNavigation />}
          </>
        )}
      </div>
      <AlertUI />
      <StaticToastUI />
      <ScrollToTop />
      <NProgress />
    </div>
  );
};

export default Layout;

export const getLayout = (
  Page: NextComponentType<NextPageContext, any, PageProps>,
  pageProps: PageProps,
  props?: LayoutProps
) => (
  <Layout {...props} pageProps={pageProps}>
    <Page {...pageProps} />
  </Layout>
);

export const noLayout = (
  Page: NextComponentType<NextPageContext, any, PageProps>,
  pageProps: PageProps
) => (
  <>
    <Page {...pageProps} />
    <AlertUI />
    <StaticToastUI />
  </>
);

export const embedLayout = (
  Page: NextComponentType<NextPageContext, any, PageProps>,
  pageProps: PageProps
) => <Page {...pageProps} />;

//
